import React, { useEffect, useRef, useState } from 'react';
import { SendTransactionState, SendTransactionStateRecipient } from '../wallet/SendTransaction';
import { useWalletSend } from '../../../hooks/useWalletSend';
import Loader from './Loader';
import { ButtonSize } from '../../../components/Button';
import { useWallet } from '../../../hooks/useWallet';
import { Button } from '../../../components/Button';
import { ButtonVariant } from '../../../config/colors';
import { formatAmountWithSATS, formatNumberWithCommas } from '../../../utils/Utils';
import { InputSearch } from '../../../components/InputSearch';
import { payFromTransactionState } from '../wallet/components/SendTxStep3';
import { getAmountNumber } from '../wallet/components/SendTxStep2';
import { Transaction } from '../../../models/Transactions';
import Portal from '../../../components/Portal';

interface DialogSendWalletProps {
  visible: boolean;
  recipient?: SendTransactionStateRecipient;
  onClose: () => void;
  position: { top: number, right: number };
}

const DialogSendWallet: React.FC<DialogSendWalletProps> = ({ visible, recipient, onClose, position }) => {
  const {loading, setLoading, error, setError, scanRecipient} = useWalletSend();
  const {balance, refresh, addTransaction} = useWallet();
  const [state, setState] = useState<SendTransactionState | null>(null);
  const [transaction, setTransaction] = useState<Transaction | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if((state?.amount ?? 0) > (getMaxAmountSpendable()))
        setError('Insufficient balance');
    else
        setError('');
}, [state?.amount, balance]);

  useEffect(() => {
    if(!visible) {
      handleAmountChange('');
      setTransaction(null);
    } else {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [visible])

  const handleChangeComment = (comment: string) => {
    const maxLength = state?.scanData?.commentAllowed ?? 250;
    const croppedComment = comment.slice(0, maxLength);
    setState({...state, comment: croppedComment});
  }

  const handleAmountChange = (newAmount: string) => {
    const newAmountNumber = getAmountNumber(newAmount);
    if(newAmountNumber <= 9999999)
      setState({...state, amount: newAmountNumber});
  };

  const handleButtonClick = async () => {
    setLoading(true);
    setError('');

    try {
      if(state) {
        const transaction = await payFromTransactionState(state);

        if(transaction) {
          refresh();
          setTransaction(transaction);
          addTransaction(transaction);
        }
      }
    } catch (err: any) {
      setError(err.message);
    }

    setLoading(false);
  }

  const getMaxAmountSpendable = () : number => {
    if(recipient?.bulkPayment)
        return state?.recipient?.bulkPayment?.maxSendablePerRecipient ?? 0;
    else
        return state?.scanData?.maxSendable ?? 0;
  }

  useEffect(() => {
    if(recipient) {
      scanRecipient(recipient)
        .then((data) => {
          if(data) {
            setState(data);
          }
        })
    }
  }, [recipient])

  if(visible)
    return (
      <Portal isOpen={visible} onClose={onClose} className="w-[300px] fixed m-4" style={{ top: position.top, right: position.right }}>
        <div className='flex flex-col h-full bg-brand-bg-2 rounded-3xl p-4 min-h-[250px] justify-center align-center'>
          {
            loading ? <Loader small /> :
            transaction ? <div className="flex flex-col justify-center items-center flex-1 gap-6 p-4">
              <div className="flex flex-col items-center">
                  <img src={"/images/payment_success_transparent.png"} alt="" className="w-[120px]" />
              </div>

              {
                Math.abs(transaction?.amount ?? 0) > 0 ? <div className="flex flex-col items-center">
                  <div className={`font-semibold text-3xl`}>
                      {formatAmountWithSATS(Math.abs(transaction?.amount ?? 0))}
                  </div>
                </div> : <div>
                  <div className={`font-semibold text-xl text-center`}>Zap in process...</div>
                  <div className={`text-center`}>You may close this tab</div>
                </div>
              }

              <div className='flex flex-row justify-center'>
                <Button
                  variant={ButtonVariant.primary}
                  size={ButtonSize.medium}
                  onClick={onClose}
                >
                    CONFIRMED!
                  </Button>
                </div>
              
            </div> : 
              state ? <div className='flex-grow text-brand-text-2 flex flex-col justify-center align-center gap-4'>
                <InputSearch
                  ref={inputRef}
                  placeholder="Amount"
                  value={formatNumberWithCommas(state?.amount && state?.amount > 0 ? state?.amount?.toString() : '')}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleAmountChange(e.target.value)}
                  onClear={() => handleAmountChange('')}
                  inputMode="numeric"
                />

                {balance && balance > 0 && (
                  <div className="flex flex-row justify-center">
                      <Button
                          size={ButtonSize.small}
                          onClick={() => handleAmountChange(formatNumberWithCommas(Math.floor(getMaxAmountSpendable()).toString() ?? ''))}
                          variant={ButtonVariant.unselected}
                          type="button"
                      >
                          Max {formatAmountWithSATS(Math.floor(getMaxAmountSpendable()) ?? 0)}
                      </Button>
                  </div>
                )}

                {
                  ((state.scanData?.commentAllowed && state.scanData?.commentAllowed > 0) ||
                  state.recipient?.bulkPayment) && (
                    <div className='mt-4'>
                      <InputSearch
                          placeholder="Message"
                          value={state.comment ?? ''}
                          onClear={() => handleChangeComment('')}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChangeComment(e.target.value)}
                      />
                  </div>
                  )
                }

                {error && error !== '' && <div className="text-brand-red text-center">{error}</div>}

                <div className='flex flex-row justify-center mt-4'>
                  <Button
                    variant={ButtonVariant.primary}
                    size={ButtonSize.medium}
                    onClick={handleButtonClick}
                    disabled={loading || state?.amount === 0 || state?.amount === undefined}
                    loading={loading}
                  >
                    SEND IT
                  </Button>
                </div>
              </div> :
              null
          }
        </div>
      </Portal>
    );
  
  return null;
};

export default DialogSendWallet;